import type React from 'react';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button, TextButton } from '@xing-com/button';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useBannerContext } from '@xing-com/crate-jobs-components-banner';
import { useToggleFollowCompany } from '@xing-com/crate-jobs-hooks';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { IconArrowRight } from '@xing-com/icons';
import { spaceM } from '@xing-com/tokens';

type EmployerActionsProps = {
  companyId: string;
  isFollowing: boolean;
  jobId: string;
  link?: string;
};

const ActionsWrapper = styled.div`
  display: flex;
  gap: ${spaceM};
  align-items: flex-start;
`;

export const EmployerActions: React.FC<EmployerActionsProps> = ({
  companyId,
  isFollowing,
  jobId,
  link,
}) => {
  const { isLoggedOut } = useLoginState();
  const { showErrorBanner, showSuccessBanner } = useBannerContext();
  const onCompleted = useCallback(() => {
    showSuccessBanner(
      isFollowing
        ? 'JOBS_JOB_DETAILS_FOLLOW_COMPANY_NOTIFICATION_A11Y'
        : 'JOBS_JOB_DETAILS_UNFOLLOW_COMPANY_NOTIFICATION_A11Y'
    );
  }, [isFollowing, showSuccessBanner]);
  const onError = useCallback(() => {
    showErrorBanner(
      isFollowing
        ? 'JOBS_JOB_DETAILS_UNFOLLOW_COMPANY_NOTIFICATION_ERROR_A11Y'
        : 'JOBS_JOB_DETAILS_FOLLOW_COMPANY_NOTIFICATION_ERROR_A11Y'
    );
  }, [isFollowing, showErrorBanner]);
  const { onToggleFollow } = useToggleFollowCompany({
    companyId,
    currentIsFollowing: isFollowing,
    onCompleted,
    onError,
  });
  const trackNwt = useJobsNwtTracking();
  const { formatMessage } = useIntl();
  const followButtonLabel = formatMessage({
    id: isFollowing
      ? 'JOBS_JOB_DETAILS_COMPANY_MODULE_UNFOLLOW_A11Y_LABEL'
      : 'JOBS_JOB_DETAILS_COMPANY_MODULE_FOLLOW_A11Y_LABEL',
  });

  const handleToggleFollow = (event: MouseEvent): void => {
    event.stopPropagation();

    trackNwt({
      event: isFollowing ? 'unfollowed' : 'followed',
      eventSchema: 'extended',
      page: 'jobs/detail',
      itemUrn: `surn:x-xing:companies:company:${companyId}`,
      originalItemUrn: `surn:x-xing:jobs:posting:${jobId}`,
      element: `jdp_company_insights_${isFollowing ? 'unfollow' : 'follow'}`,
    });
    onToggleFollow();
  };

  const onMoreVacanciesClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      trackNwt({
        event: 'opened',
        eventSchema: 'basic',
        page: 'jobs/detail',
        element: 'more_vacancies',
        itemUrn: `surn:x-xing:jobs:posting:${jobId}`,
      });

      window.location.assign(`${link}/jobs`);
    },
    [jobId, link, trackNwt]
  );

  return (
    <ActionsWrapper>
      {!isLoggedOut ? (
        <Button
          variant="secondary"
          size="small"
          sizeWide="small"
          // @ts-expect-error FIXME: SC6
          onClick={handleToggleFollow}
          aria-label={followButtonLabel}
        >
          <FormattedMessage
            id={
              isFollowing
                ? 'JOBS_JOB_DETAILS_UNFOLLOW_COMPANY'
                : 'JOBS_JOB_DETAILS_FOLLOW_COMPANY'
            }
          />
        </Button>
      ) : null}
      {link ? (
        <TextButton
          size="small"
          icon={IconArrowRight}
          onClick={onMoreVacanciesClick}
          iconAfter
        >
          <FormattedMessage id="JOBS_JOB_DETAILS_COMPANY_ALL_JOBS" />
        </TextButton>
      ) : null}
    </ActionsWrapper>
  );
};
